<template>
    <div class="full-height-column">
        <iframe
            ref="reportIframe"
            class="report-iframe"
            src="report-assets/report.html"
            @load="handleFrameLoaded"
        ></iframe>

        <button type="button" class="btn" @click="generatePdf">{{ $t('report.form.generatePDF') }}</button>
    </div>
</template>

<script lang="ts">
import { useAppSettingsStore } from '@/store/app-settings';
import { usePatientStore } from '@/store/patient';
import { useRequestDataStore } from '@/store/request-data';
import { useReportStore } from '@/store/report';
import { defineComponent, ref } from 'vue';
import * as html2pdf from 'html2pdf.js';
import { useI18n } from 'vue-i18n';
import { DosageSteadyState } from '@/service/models/common/query-dosage/parts/dosage-steady-state';

export default defineComponent({
    props: {
        chartImage: {
            type: String,
            required: true,
        },
    },

    setup(props)
    {
        const i18n = useI18n();
        const reportIframe = ref<HTMLIFrameElement|null>();
        const patientStore = usePatientStore();
        const requestDataStore = useRequestDataStore();
        const reportStore = useReportStore();
        const appSettingsStore = useAppSettingsStore();

        const adjustmentDate = requestDataStore.adjustmentDate ?? new Date();
        const lastDosage = requestDataStore.getDosageAt(adjustmentDate);
        const lastDosageDose = lastDosage?.dosage.getInnerDosageChoice();

        function generatePdf(): void
        {
            if (!reportIframe.value || !reportIframe.value.contentDocument) {
                return;
            }

            // Generate PDF and prompt user to save it. Actually this library generates an image, which
            // is heavey and doesn't allow to search for text in the document. Maybe some research could
            // be done to find a better alternative.
            html2pdf()
                .set({
                    image: {type: 'png'},
                })
                .from(reportIframe.value.contentDocument.documentElement)
                .save('report.pdf');
        }

        function handleFrameLoaded(): void
        {
            const frameWin = reportIframe.value?.contentWindow;
            const frameDoc = reportIframe.value?.contentDocument;

            if (!frameDoc || !frameWin) {
                throw Error("Failed to load report iframe");
            }

            // Prepare the data object that will be fed into the iframe
            const frameData: Record<string, unknown> = {
                adjustments: requestDataStore.selectedAdjustmentDosages.map((adjustment) => {
                    const dosageChoice = adjustment.lastDosage?.getInnerDosageChoice();

                    return {
                        date: adjustment.start,
                        dose: dosageChoice?.getDoseValueForDisplay(),
                        doseUnit: dosageChoice?.getDoseUnitForDisplay(),
                        infusion: dosageChoice?.getInfusionTimeForDisplay()?.toTextString() ?? "0h00",
                        interval: dosageChoice?.getIntervalForDisplay().toTextString(),
                        route: i18n.t(`administrationRoute.${dosageChoice?.getRouteForDisplay().administrationRoute}`),
                    };
                }),
                analysis: {
                    expectedness: reportStore.expectedness,
                    prediction: reportStore.prediction,
                    remonitoring: reportStore.remonitoring,
                    suitability: reportStore.suitability,
                    warning: reportStore.warning,
                },
                analyst: {
                    title: appSettingsStore.analystTitle,
                    firstName: appSettingsStore.analystFirstName,
                    lastName: appSettingsStore.analystLastName,
                    instituteName: appSettingsStore.analystAffiliation,
                    instituteAddress: "",
                    role: appSettingsStore.analystRole,
                    phones: (appSettingsStore.analystPhone.length === 0) ? [] : [
                        { number: appSettingsStore.analystPhone },
                    ],
                },
                covariates: requestDataStore.getCovariatesAt(adjustmentDate).map((covariate) => {
                    return {
                        id: covariate.id,
                        name: requestDataStore.selectedDrugModel?.model.getDrugCovariateById(covariate.id)?.name ?? "",
                        value: `${covariate.value}${covariate.unit}`,
                    };
                }),
                drugCovariates: requestDataStore.selectedDrugModel?.model.covariates.map((covariate) => {
                    return {
                        id: covariate.id,
                        name: covariate.name,
                        value: `${covariate.covariateValue.value}${covariate.unit}`,
                    };
                }),
                drugName: requestDataStore.selectedDrugName,
                graphPath: props.chartImage,
                lastDosage: `${lastDosageDose?.getDoseValueForDisplay()} ${lastDosageDose?.getDoseUnitForDisplay()}`,
                lastDosageDate: (lastDosage?.dosage.dosageRepeat instanceof DosageSteadyState) ? lastDosage.dosage.dosageRepeat.lastDoseDate : lastDosage?.start,
                lastDosageInterval: lastDosage?.dosage.getInnerDosageChoice().getIntervalForDisplay(),
                mandator: {
                    firstName: patientStore.physicianFirstName,
                    instituteAddress: patientStore.physicianAddress,
                    instituteName: patientStore.physicianAffiliation,
                    lastName: patientStore.physicianLastName,
                    phones: (patientStore.physicianPhone.length === 0) ? [] : [
                        { number: patientStore.physicianPhone },
                    ],
                    title: patientStore.physicianTitle,
                },
                measures: requestDataStore.samples.map((sample) => {
                    return {
                        sampleDate: sample.date,
                        sampleId: sample.id,
                        sampleValue: `${sample.concentrations[0].value} ${sample.concentrations[0].unit}`,
                    };
                }),
                nextControl: reportStore.nextControlDate,
                parameters: [...reportStore.pkParameters.parameters.values()].map((pkParam) => {
                    return {
                        id: pkParam.id,
                        unit: pkParam.unit,
                        popValue: pkParam.population,
                        aposterioriValue: pkParam.aPosteriori ?? '-',
                        aprioriValue: pkParam.aPriori ?? '-',
                    }
                }),
                patient: {
                    address: patientStore.address,
                    birthday: patientStore.birthDate,
                    externalId: patientStore.identifier,
                    firstName: patientStore.fullName,
                    gender: patientStore.numericGender,
                    lastName: patientStore.lastName,
                },
                proposedDosage: requestDataStore.selectedAdjustmentDosages.map((dosage) => dosage.getDescriptionString()).join('<br />'),
                //TODO: When should these info be retrieved ?
                steadyStateAUC24: `${reportStore.steadyStateStats?.auc24?.toFixed(1)} mg*h/l`,
                steadyStateMax: `${reportStore.steadyStateStats?.peak?.toFixed(2)} mg/l`,
                steadyStateMin: `${reportStore.steadyStateStats?.residual?.toFixed(2)} mg/l`,
                tucuxiVersion: process.env.VUE_APP_VERSION,
                validationDate: reportStore.validationDate,
            };

            // Call the unpdate function in the iframe to display the data
            (frameWin as any).updateFieldsFromJson(JSON.stringify(frameData)); //eslint-disable-line @typescript-eslint/no-explicit-any
        }

        return {
            reportIframe,
            handleFrameLoaded,
            generatePdf,
        };
    },
})
</script>

<style lang="scss" scoped>

.full-height-column {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding-bottom: 0.5rem;
    padding-left: 0.5rem;
}

.report-iframe {
    flex: 1;
    width: 100%;
    border: 1px solid $gray-600;
    margin-bottom: 0.5rem;
}

</style>
